import { DEFAULT_MAX_EXPOSURE } from '../constants';
import { isMarketingPlanManualMode, shouldApplyDrawLimits } from './resolvePlanFunding';

export { shouldApplyDrawLimits };

export function parseOptionalLimit(value) {
  const n = Number(value);
  return value != null && value !== '' && Number.isFinite(n) && n > 0 ? n : null;
}

export function resolveRequiredMaxExposure(value) {
  const parsed = parseOptionalLimit(value);
  return parsed == null ? DEFAULT_MAX_EXPOSURE : parsed;
}

export function resolveFacilitySize(state) {
  const override = parseOptionalLimit(state?.facilitySize);
  return override == null
    ? resolveRequiredMaxExposure(state?.maxExposure)
    : override;
}

export function stateWithMaxExposure(state, maxExposure) {
  return {
    ...state,
    maxExposure,
    facilitySize: maxExposure,
  };
}

export function resolveFacilityFeeAmount(state) {
  const facilitySize = resolveFacilitySize(state);
  const feePct = Number(state?.facilityFeePct) || 0;
  return Math.round((facilitySize * feePct) / 100);
}

/** Forward-filled max exposure covenant active at plan row index. */
export function resolveMaxExposureAtPlanMonth(state, planIndex) {
  const globalDefault = resolveRequiredMaxExposure(state?.maxExposure);
  let active = globalDefault;
  const overrides = state?.planMaxExposure ?? [];

  for (let index = 0; index <= planIndex && index < overrides.length; index += 1) {
    const rowValue = overrides[index];
    if (rowValue != null && rowValue !== '') {
      const parsed = parseOptionalLimit(rowValue);
      if (parsed != null) {
        active = parsed;
      }
    }
  }

  return active;
}

export function resolveMaxExposureAtOutputMonth(state, outputMonth) {
  const globalDefault = resolveRequiredMaxExposure(state?.maxExposure);

  if (isMarketingPlanManualMode(state)) {
    const planIndex = outputMonth - 1;
    if (planIndex < 0) {
      return null;
    }
    return parseOptionalLimit(state?.planMaxExposure?.[planIndex]);
  }

  return globalDefault;
}

/** Draw for plan row i lands in output month i + 1. */
export function resolveMaxMonthlyDrawForPlanMonth(state, planIndex) {
  if (!shouldApplyDrawLimits(state)) {
    return null;
  }
  if (planIndex < 0) {
    return null;
  }
  const rowValue = state?.planMaxMonthlyDraw?.[planIndex];
  const rowLimit =
    rowValue != null && rowValue !== '' ? parseOptionalLimit(rowValue) : null;
  return rowLimit ?? parseOptionalLimit(state?.maxMonthlyDraw);
}

export function resolveMaxMonthlyDrawAtOutputMonth(state, outputMonth) {
  return resolveMaxMonthlyDrawForPlanMonth(state, outputMonth - 1);
}

export function buildMaxExposureByOutputMonth(state, outputMonthCount) {
  return Array.from({ length: outputMonthCount }, (_, outputMonth) =>
    resolveMaxExposureAtOutputMonth(state, outputMonth)
  );
}

export function buildMaxMonthlyDrawByOutputMonth(state, outputMonthCount) {
  if (!shouldApplyDrawLimits(state)) {
    return Array.from({ length: outputMonthCount }, () => null);
  }
  return Array.from({ length: outputMonthCount }, (_, outputMonth) =>
    resolveMaxMonthlyDrawAtOutputMonth(state, outputMonth)
  );
}

export function planHasAnyExposureLimit(state) {
  if (!isMarketingPlanManualMode(state)) {
    return parseOptionalLimit(state?.maxExposure) != null;
  }
  return (state?.planMaxExposure ?? []).some(
    (value) => parseOptionalLimit(value) != null
  );
}

export function planHasAnyMonthlyDrawLimit(state) {
  if (!shouldApplyDrawLimits(state)) {
    return false;
  }
  if (parseOptionalLimit(state?.maxMonthlyDraw) != null) {
    return true;
  }
  return (state?.planMaxMonthlyDraw ?? []).some(
    (value) => parseOptionalLimit(value) != null
  );
}
